/** @module IglooTracker **/

/**
 * Export the IglooTracker Module
 * @param {Igloo} igloo The igloo module
 * @return {Object} The public functions:
 * [addEvent]{@link module:IglooTracker~add_event},
 * [enableDwell]{@link module:IglooTracker~enable_dwell}, and
 * [queue]{@link module:IglooTracker~queue}
 */
module.exports = function(igloo) {
  'use strict';

  /**** Variable Declarations ****/

  /**
   * The key used for local storage for session data
   * @type {String}
   */
  var session_key = 'igloo_session_';

  /**
   * URL to post the tracking data to
   * @type {String}
   */
  var q_url = '//yeti.predictinteractive.com/';

  /**
   * Functions to update dynamic values
   * @type {Object}
   */
  var update_functions = {
    /**
     * Return the current number of pageviews in the session
     * @param  {Integer} partner The partner to get the session value for
     * @return {Integer} The number of pageviews associated with this session
     */
    s_pv: function(partner) {
      return get_current_session(partner).pageview || 1;
    }, // --> s_pv

    /**
     * Returns the amount of time spent on the page
     * @return {Integer} The amount of time since the tracker loaded in seconds
     */
  };

  /**
   * Returns the amount of time between start time and now
   * @param  {Integer} start_time The start time of the embed
   * @return {Integer} The amount of time since the tracker loaded in seconds
   */
  var time_since_tracker_start = function(start_time) {
    return Math.round(((new Date()).getTime() - start_time) / 1000);
  };

  /**
   * Igloo Logger
   * @type {module:IglooLogger}
   */
  var log = require('./IglooLogger')(igloo, 'PREDICT Tracker');

  /**
   * The keys to send for each type of event
   * @type {Object}
   */
  var event_keys = {
    pageview: ['b_x', 'b_y', 'd', 'e', 'g', 'm', 'p', 's_pv', 'top', 'u',],
    dwell: ['d', 'e', 'g', 'm', 'p', 'pl', 'top', 'w', 'u', 'z', 'te'],
    widget: ['d', 'e', 'g', 'm', 'p', 'pl', 'top', 'w', 'u', 'z',],
    widget_visible: ['d', 'e', 'g', 'm', 'p', 'pl', 'top', 'w', 'u', 'z',],
    widget_clicked: ['d', 'e', 'g', 'l', 'm', 'p', 'pl', 'top', 'w', 'u', 'z',],
    widget_interaction: ['d', 'e', 'g', 'l', 'm', 'p', 'pl', 'top', 'w', 'u', 'z', 'f', 'e_id'],
    widget_list: ['d', 'e', 'g', 'l', 'm', 'p', 'pl', 'top', 'w', 'u', 'z',],
    bids: ['d', 'e', 'event', 'g', 'm', 'p', 'pl', 'top', 'v', 'w', 'u', 'z',],
    creative: ['e', 'c_id', 'p', 'z', 'au_id', 'bidder', 'cpm', 'a_cpm', 'ad', 'ad_url', 'd', 'url', 'ad_size', 'm']
  };

  /**
   * The strings to log when an event is passed
   * @type {Object}
   */
  var event_strings = {
    pageview: 'Pageview Event ({{p}})',
    dwell: 'Dwell Event ({{p}}): {{top}}s',
    widget: 'Widget Embed Event: {{w}}',
    widget_visible: 'Widget Visible Event: {{w}}',
    widget_clicked: 'Widget Clicked Event: {{w}}, {{l}}',
    widget_interaction: 'Widget Interaction Event: {{w}}, {{l}}',
    widget_list: 'Widget List Event: {{w}}, {{l}}',
    bids: 'Auction Result Event: {{w}}',
    creative: 'Creative Event: {{bidder}} {{c_id}}',
  };

  /**
   * The seconds to make dwell events at. Not a constant because the array is
   * modified whenever a dwell event is sent.
   * @type {Array}
   */
  var dwell_events = [
    5, 10, 15, 20, // 5s interval
    30, // 10s interval
    45, 60, // 15s intervals
  ];

  /**
   * Queue of analytics messages
   * @type {Array}
   */
  var queue = [];

  /**
   * The data that is currently in the object
   * @type {Object}
   */
  var object_data = {
    ad: null, // The ad creative string
    ad_hash: null, // The md5 of the ad
    ad_id: null, // The string used to generate the ad
    ap: null, // Ad provider name
    b: null, // Bid amount
    b_x: igloo.top.innerWidth || igloo.top.document.body.clientWidth,
    b_y: igloo.top.innerHeight || igloo.top.document.body.clientHeight,
    c: navigator.cookieEnabled,
    d: igloo.top.location.hostname,
    e: null, // Event title
    g: igloo.utils.guid(),
    l: null, // A string identifying the list
    m: igloo.browser.mobile,
    p: null, // Partner ID
    pl: null, // Placement ID
    r: null, // Response time
    s_pv: null, // # of pageviews this session
    te: null, // Time elapsed (dwell)
    to: null, // Timeout of the auction
    top: null, // Time on the page
    u: igloo.top.location.href.replace(/^https?:\/\/(www\.|)(.*?)\/?(\?.*|)$/, '$2'), // Sanitized URL
    v: null, // Ad zone visibility
    w: null, // Widget type
    z: null, // The zone ID
    e_id: null, // The embed ID
  };

  /**
   * The object that holds the yet-to-be-fired dwell events by embed ID
   * @type {Object}
   */
  var embed_dwell_events = {};

  /**
   * The timestamp of when the next request should be sent
   * @type {Number}
   */
  var next_send_timestamp = null;

  /**
   * How long we should wait for other messages to batch (in ms)
   * @type {Number}
   */
  var max_message_delay = 500;

  /**** ENSIGHTEN VARIABLES ****/

  /**
   * Domains to drop ensighten tracking pixels on
   * @type {Object}
   */
  var ensighten_doms = {
    'latimes.com': '1',
    'chicagotribune.com': '2',
    'orlandosentinel.com': '3',
    'sun-sentinel.com': '4',
    'baltimoresun.com': '5',
    'courant.com': '6',
    'dailypress.com': '7',
    'mcall.com': '8',
    'carrollcountytimes.com': '9',
    'capitalgazette.com': '10',
    'citypaper.com': '11',
    'hoylosangeles.com': '12',
    'redeyechicago.com': '13',
    'growthspotter.com': '14',
    'vagazette.com': '15',
    'southflorida.com': '16',
    'ctnow.com': '17',
    'sandiegouniontribune.com': '18',
    'discoversd.com': '19',
    'lajollalight.com': '20',
    'delmartimes.com': '21',
    'ranchosantafereview.com': '22',
    'pacificsandiego.com': '23',
    'tronc.com': '24',
    'forsalebyowner.com': '25',
    'motiv8agency.com': '26',
    'tribunecontentagency.com': '27',
    'tidewaterreview.com': '28',
    'tribpubads.com': '29',
    'tribpubdev.com': '30',
  };

  /**
   * The URL to send ensighten tracking to
   * @type {String}
   */
  var ensighten_url = '//activate.{{DOM}}/no-content?cw_pch=1&cw_pei=2&cw_pbrd={{ID}}&ci_paic=1&ci_pdic=1&cw_pve=5&pulse2=' + encodeURIComponent(igloo.top === window.top ? igloo.top.location.href : igloo.top.document.referrer) + '&pulse92=' + encodeURIComponent(window.trb && window.trb.exopid || '');

  /**
   * The events to drop an ensighten tracking pixel for
   * @type {Array}
   */
  var ensighten_events = ['widget'];

  /**** NUVIAD VARIABLES ****/

  /**
   * The URL to use for the nuviad tracking pixel
   * @type {String}
   */
  var nuviad_url = 'https://notify.nuviad.com/conversion/actor_I7RquqPLdy76JV6eBaN1QwkcKXDa/{{xclid}}?stage={{xstage}}&revenue={{xrevenue}}&img=true';

  /**** COMET VARIABLES ****/

  /**
   * The URL of the location to POST the COMET data to
   * @type {String}
   */
  var comet_url = 'https://ad.afy11.net/ad?mode=19';

  /**** Watching and Sending Functions ****/

  /**
   * Check for new events to be sent out
   * @return {Bool} Whether new events were found
   */
  function check_events() {
    // Check for any new dwell events
    var dwell_keys = Object.keys(embed_dwell_events);
    var dwell_event;
    var dwell_elapsed;
    // Loop through all embed IDs
    dwell_keys.forEach((embed_id) => {
      // Loop through all of the new events
      while (
        typeof embed_dwell_events[embed_id].events[0] !== 'undefined'
        && time_since_tracker_start(embed_dwell_events[embed_id].start_time) >= embed_dwell_events[embed_id].events[0]
      ) {
        // Set the dwell event
        dwell_event = embed_dwell_events[embed_id].events.shift();
        dwell_elapsed = dwell_event - embed_dwell_events[embed_id].last;
        embed_dwell_events[embed_id].last = dwell_event;

        // Add the dwell events
        add_event({
          top: dwell_event,
          te: dwell_elapsed,
          z: embed_dwell_events[embed_id].placement_id,
          p: embed_dwell_events[embed_id].partner_id,
          w: embed_dwell_events[embed_id].widget_type,
          e: 'dwell',
        });
      }
    });

    // Check for the next send time to be set
    if (next_send_timestamp === null || (new Date()).getTime() <= next_send_timestamp) {
      return false;
    }

    // Check for unsent events
    for (var i = 0; i < queue.length; i++) {
      if (queue[i].sent === 0 && queue[i].tries <= 5) {
        send_events(queue);
        next_send_timestamp = null;
        return true;
      }
    }
    return false;
  } // --> check_events

  /**
   * Send (or retry) items in the queue
   * @param  {Array}   event_queue The queue of events to check for items to
   *                               send
   * @return {Integer}             The number of events sent to the API
   */
  function send_events(event_queue) {
    log.trace('send_events', arguments);

    // Make the empty event loop
    var post_body = [];
    var posted_events = [];

    // Loop through the queue
    for (var i = 0; i < event_queue.length; i++) {
      // Skip items that have already been sent or have been tried too much
      if (event_queue[i].sent !== 0 || event_queue[i].tries > 5) {
        continue;
      }

      // Increment the try counter
      event_queue[i].tries++;
      posted_events.push(i);

      // Check for a bot
      if (igloo.browser.bot) {
        // If bot, mark as sent and skip
        event_queue[i].sent = 1;
        continue;
      }

      // Add to the request
      post_body.push(event_queue[i].data);

      // Mark as sending
      event_queue[i].sent = -1;
    }

    // Prepare the XHR request
    var xhr;
    if (window.XMLHttpRequest) {
      xhr = new window.XMLHttpRequest();
    } else {
      xhr = new window.ActiveXObject('Microsoft.XMLHTTP');
    }

    // Add the event listener
    xhr.onreadystatechange = send_event_handler.bind(undefined, xhr, event_queue, posted_events);

    // Send the request
    xhr.open('POST', q_url, true);
    xhr.setRequestHeader('Content-Type', 'application/x-www-form-urlencoded');
    xhr.send(JSON.stringify(post_body));

    // Return the number of sent events
    return posted_events.length;
  } // --> send_events

  /**
   * Handler for the XHR request that sends the analytics data
   * @param  {XMLHttpRequest} xmlHttp     The request from the analytics data
   * @param  {Array}          event_queue The queue of events the indexes are
   *                                      from
   * @param  {Integer}        indexes     The indexes in the queue of the items
   *                                      in the HTTP request
   * @return {Boolean}                    Whether the request was success or not
   */
  function send_event_handler(xmlHttp, event_queue, indexes) {
    log.trace('send_event_handler', arguments);

    // Check for the request being done
    if (xmlHttp.readyState !== XMLHttpRequest.DONE) {
      return true;
    }

    // Check for success
    var new_flag = 1;
    if ([204, 200, 420].indexOf(xmlHttp.status) === -1) {
      new_flag = 0;
      next_send_timestamp = (new Date()).getTime() + max_message_delay;
    }

    // Check for 200 response
    if (xmlHttp.status === 420) {
      log.warn('Yeti Error', xmlHttp.responseText);
    }

    // Set all of the flags
    for (var i = 0; i < indexes.length; i++) {
      event_queue[indexes[i]].sent = new_flag;
    }

    return new_flag === 1;
  } // --> send_event_handler

  /**** Queue Management Functions ****/

  /**
   * Add an event to the queue to be sent out
   * @param {Object} data The parameters from the event
   */
  function add_event(data) {
    log.trace('add_event', arguments);

    // Check for the nuviad event
    if (data.e === 'nuviad' || data.e === '2') {
      add_nuviad_pixel(data);
      return false;
    }

    // Check for the getIntent pixel
    if (data.e === 'getintent' || data.e === '3') {
      add_getintent_pixel(data);
      return false;
    }

    // Set the timestamp for sending events if needed
    if (next_send_timestamp === null) {
      next_send_timestamp = (new Date()).getTime() + max_message_delay;
    }

    // Check for an event that will affect the session
    update_session(data);

    // Check for an event to drop an ensighten pixel on
    add_ensighten_pixel(data);

    // Check for an event to send a COMET pixel for
    add_comet_pixel(data);

    // Update the object data
    update_object_data(data, object_data);

    // Build the queue item
    var queue_item = {
      sent: 0,
      tries: 0,
      data: {},
    };

    // Get the event type (different for bids)
    var e_type = data.event || data.e;

    // Get the string to log
    var log_string = event_strings[e_type] || 'Unknown Event: ' + e_type;

    // Check for a valid event type
    if (typeof event_keys[e_type] === 'undefined') {
      log.warn('Invalid Event', data);
      return false;
    }

    // Save all of the needed values
    event_keys[e_type].forEach((key) => {
      // Save the data into the message
      queue_item.data[key] = object_data[key];

      // Replace in the string
      log_string = log_string.replace('{{' + key + '}}', object_data[key]);
    });

    // Push to the queue
    queue.push(queue_item);

    // Log the string
    log.verbose(log_string);

    // Set the zone ID to null
    object_data.z = null;
  }

  /**
   * Add an ensighten tracking pixel if needed
   * @param  {Object}  data The event data
   * @return {Boolean}      Whether a pixel was embedded or not
   */
  function add_ensighten_pixel(data) {
    // Check for an ensighten event
    if (typeof data.e !== 'undefined' && ensighten_events.indexOf(data.e) !== -1 && typeof ensighten_doms[data.d] !== 'undefined') {
      // Create the image
      var ensighten_image = document.createElement('img');
      igloo.utils.setElementAttributes(ensighten_image, {
        src: ensighten_url.replace(/{{[^}]*}}/g, function(capture) {
          switch (capture) {
            case '{{ID}}':
              return ensighten_doms[data.d];
            case '{{DOM}}':
              return data.d;
          }
          return '';
        }),
        width: 1,
        height: 1,
        style: {
          display: 'none',
        },
      });

      return true;
    }

    return false;
  } // --> add_ensighten_pixel

  /**
   * Add a nuviad tracking pixel
   * @param  {Object}     data The event data
   * @return {DOMElement}      The embedded pixel
   */
  function add_nuviad_pixel(data) {
    // Create the image
    var nuviad_image = document.createElement('img');
    igloo.utils.setElementAttributes(nuviad_image, {
      src: nuviad_url.replace(/{{([^}]*)}}/g, function(capture, value) {
        // Check for the value in the data
        if (typeof data[value] !== 'undefined') {
          return data[value];
        }

        // Return blank if none is found
        return '';
      }),
      width: 1,
      height: 1,
      style: {
        display: 'none',
      },
    });

    return nuviad_image;
  } // --> add_nuviad_pixel

  /**
   * Add a GetIntent tracking pixel
   * @param  {Object} data The event data
   * @return {Object}      The embedded pixel
   */
  function add_getintent_pixel(data) {
    var return_object = {};

    // Determine if the script has been embedded
    if (typeof igloo.top.__GetI === 'undefined') {
      // Define the que
      igloo.top.__GetI = [];

      // Embed the script
      var domain = 'px.adhigh.net';
      var src = '//' + domain + '/p.js';
      var script = document.createElement('script');
      script.type = 'text/javascript';
      script.src = src;
      igloo.top.document.body.appendChild(script);
    }

    // Add the pixel
    var pixel = {
      type: 'CONVERSION',
      site_id: '3384',
      order: '',
      transaction_id: '',
      revenue: typeof data.revenue === 'undefined'
        ? ''
        : data.revenue,
      pixel_id: ['load', 'auction', 'break_even'].indexOf(data.pixel),
    };

    igloo.top.__GetI.push(pixel);
    return_object.pixel = pixel;

    // Fire their "image" tracking pixel
    var pixel1 = "https://px.adhigh.net/p/tracking.gif?syncs=default&site_id=3360&pixel_id=" +
    ['load', 'auction', 'break_even'].indexOf(data.pixel);
    if (data.pixel === 'auction') {
      pixel1 = pixel1 + "&revenue=" + data.revenue;
    }
    var pixel2 = "https://px.adhigh.net/p/tracking.gif?syncs=bsw&syncs_only=true";

    var attrObj = {
      src: pixel1,
      width: 1,
      height: 1,
      style: {
        display: 'none',
      },
    };

    var pixel1_image = document.createElement('img');
    igloo.utils.setElementAttributes(pixel1_image, attrObj);
    // Limit to changing one thing at a time; first adding second pixel
    // igloo.top.document.body.appendChild(pixel1_image);
    return_object.pixel1_image = pixel1_image;

    attrObj.src = pixel2;
    var pixel2_image = document.createElement('img');
    igloo.utils.setElementAttributes(pixel2_image, attrObj);
    // Limit to changing one thing at a time; first adding second pixel
    // igloo.top.document.body.appendChild(pixel2_image);
    return_object.pixel2_image = pixel2_image;

    return return_object;
  } // --> add_getintent_pixel

  /**
   * POST data to the COMET tracking URL if the event meets the criteria
   * @param  {Object}  data The event data
   * @return {Boolean}      Whether the pixel was sent or not
   */
  function add_comet_pixel(data) {
    // Check for a revenue event
    if (typeof data.event === 'undefined' || data.event !== 'bids') {
      return false;
    }

    // Default to false (no event sent)
    var return_value = false;

    // Loop through the bids
    for (var bid = 0; bid < data.e.length; bid++) {
      // Check for reasons to skip
      if (
        data.e[bid].e !== 'win' || // Not a winning bid
        typeof data.e[bid].iid === 'undefined' || // The comet tracking pixel isn't there
        data.e[bid].ap === 'predict' // A passback won the auction
      ) {
        // Skip this bid
        continue;
      }

      // Build the POST object
      var comet_post_data = {
        p_id: data.p,
        bid_cpm: data.e[bid].bid,
        win_cpm: data.e[bid].b,
        impression_id: data.e[bid].iid,
        domain: data.d,
        size: data.e[bid].size,
        viewable: data.v,
        ap_name: data.e[bid].ap,
      };

      // Prepare the POST request
      var xhr;
      if (window.XMLHttpRequest) {
        xhr = new XMLHttpRequest();
      } else {
        xhr = new window.ActiveXObject('Microsoft.XMLHTTP');
      }

      // Send the request
      xhr.open('POST', comet_url, true);
      xhr.setRequestHeader('Content-Type', 'application/x-www-form-urlencoded');
      xhr.send(JSON.stringify(comet_post_data));

      // Change the return value
      return_value = true;
    }

    return return_value;
  } // --> add_comet_pixel

  /**
   * Update the global object data with event data and update functions
   * @param  {Object} new_data     The event data
   * @param  {Object} current_data The current tracker data
   * @return {Object}              The newly current tracker data
   */
  function update_object_data(new_data, current_data) {
    // Update functions
    var keys = Object.getOwnPropertyNames(current_data);
    for (var k = 0; k < keys.length; k++) {
      // Check for an update function
      if (typeof update_functions[keys[k]] !== 'undefined') {
        current_data[keys[k]] = update_functions[keys[k]](new_data.p);
      }
    }

    // Add the data to the object
    keys = Object.getOwnPropertyNames(new_data);
    for (k = 0; k < keys.length; k++) {
      // Check for existence and not null or empty string
      if ([undefined, null, ''].indexOf(new_data[keys[k]]) !== -1) {
        continue;
      }

      // Check for 'none' which is an indication to set the field to ''
      if (['none', 'n/a'].indexOf(new_data[keys[k]]) !== -1) {
        new_data[keys[k]] = '';
      }

      // Overwrite the old data
      current_data[keys[k]] = new_data[keys[k]];
    }

    return current_data;
  } // --> update_object_data

  /**
   * Removes all dwell events so no further events are sent
   * @param  {String} embed_id The unique ID for the ad zone
   * @param  {Integer} partner_id The ID of the partner to enable dwell for
   * @param  {String} placement_id The placement ID
   * @param  {String} widget_type The widget's type
   * @return {Boolean}            Whether dwell events were enabled or not
   */
  function enable_dwell(embed_id, partner_id, placement_id, widget_type) {
    log.trace('enable_dwell', arguments);

    // Add the new dwell events
    if (typeof embed_dwell_events[embed_id] === 'undefined') {
      embed_dwell_events[embed_id] = {
        events: dwell_events.slice(0),
        last: 0,
        partner_id: partner_id,
        placement_id: placement_id,
        widget_type: widget_type,
        start_time: (new Date()).getTime(),
      };
      return true;
    }

    return false;
  } // --> enable_dwell

  /**** Session Management Functions ****/

  /**
   * Return the current session object
   * @return {Object} The current session
   */
  function get_current_session(partner) {
    // Check for session storage support
    if (typeof sessionStorage === 'undefined') {
      return {};
    }

    // Get the session storage and return it
    var current_session;
    try {
      current_session = JSON.parse(sessionStorage.getItem(session_key + partner));

      // Check for invalid JSON
      if (current_session === null) {
        throw 'JSON parse error';
      }
    } catch (e) {
      current_session = {};
    }
    return current_session;
  } // --> get_current_session

  /**
   * Sets the current session data
   * @param {Object} data The value to set as the current session
   * @return {Bool}       Whether the set succeeded or failed
   */
  function set_current_session(partner, data) {
    try {
      sessionStorage.setItem(session_key + partner, JSON.stringify(data));
      return true;
    } catch (e) {
      return false;
    }
  } // --> set_current_session

  /**
   * Update specific values of the current session
   * @param  {Object} data The event information
   * @return {Object}      The new session object
   */
  function update_session(data) {
    // Get the current values
    var current_session = get_current_session(data.p);

    // Update the keys
    if (typeof current_session[data.e] === 'undefined') {
      current_session[data.e] = 0;
    }

    // Update the value
    current_session[data.e]++;

    // Set the new session
    set_current_session(data.p, current_session);

    // Return the current session
    return current_session;
  } // --> update_session

  /**** Post Message Handlers ****/

  /**
   * Handle a post message
   * @param  {Object} post_message The postMessage event
   */
  function post_message_handler(post_message) {
    log.trace('post_message_handler', arguments);

    // Check for adlightning ad block event
    if (post_message.data.message === 'ADL_BLOCKED') {
      // test if given iglooId is valid
      var iglooId = post_message.data.adUnit;
      var regex = new RegExp('^igloo_');
      var validIglooId = regex.test(iglooId);

      if (validIglooId) {
        // look for corresponding ad zone by igloo id
        var adZoneIndex;
        for(var i = 0; i < igloo.ad_zones.length; i++) {
          if (igloo.ad_zones[i].code === iglooId) {
            adZoneIndex = i;
            break;
          }
        }
        // if ad zone found, flag ad as blocked so the unit can rotate
        if (typeof adZoneIndex !== 'undefined') {
          log.info('Ad Lightning Ad Block Event. Rotating ad unit (' + iglooId + ')', post_message);
          igloo.ad_zones[adZoneIndex].flagAdAsBlocked();
        } else {
          // if ad zone not found, throw error
          log.error('Ad Lightning Ad Block Event. Ad Zone not found for given Igloo ID.', iglooId, post_message);
        }
      } else {
        // if given igloo id is not valid, throw error
        log.error('Ad Lightning Ad Block Event. Invalid Igloo ID returned from ad lightning.', iglooId, post_message);
      }

      return true;
    }

    /*jshint maxcomplexity:false*/
    // Check the type of the event and make sure it has data
    if (post_message.type !== 'message' || typeof post_message.data !== 'object' || typeof post_message.data.action !== 'string') {
      // Exit the function
      return false;
    }

    // Handle a postMessage asking for the location
    if (post_message.data.action === 'location' && typeof post_message.data.igloo !== 'undefined') {
      // Build the return object
      var return_object = {
        original: {
          href: igloo.top.location.href,
          referrer: igloo.top.document.referrer,
        },
      };

      // Sanitize the URLs
      return_object.href = return_object.original.href.split(/[?#]/)[0];
      return_object.referrer = return_object.original.referrer.split(/[?#]/)[0];

      // Send back to the source iframe
      post_message.source.postMessage({
        action: 'location',
        data: return_object
      });
      return true;
    }

    // Handle a postMessage that gives the widget clicked event
    var widget_event_alias = {
      'widget-clicked': 'widget_clicked',
      'widget-interaction': 'widget_interaction',
      'widget-list': 'widget_list',
    };
    if (
      post_message.data.action === 'pred_tracker'
      && typeof post_message.data.pred_data === 'object'
      && typeof widget_event_alias[post_message.data.pred_data.event] !== 'undefined'
    ) {
      // Add the widget event
      post_message.data.pred_data.e = widget_event_alias[post_message.data.pred_data.event];
      delete (post_message.data.pred_data.event);

      // tag data with embed id
      var embedId = get_igloo_embed_id(post_message.data.pred_data);
      if (embedId) {
        post_message.data.pred_data.e_id = embedId;
      }

      // Check if first interaction for ad zone. If yes, then set first flag to true
      if (
        post_message.data.pred_data.e === 'widget_interaction'
        && check_if_first_interaction(post_message.data.pred_data)
      ) {
        post_message.data.pred_data.f = true;
      } else {
        post_message.data.pred_data.f = false;
      }

      add_event(post_message.data.pred_data);

      return true;
    }

    if (
      post_message.data.action === 'pred_tracker'
      && typeof post_message.data.pred_data === 'object'
      && post_message.data.pred_data.event === 'ad-rotate'
    ) {
      // Add the widget event
      post_message.data.pred_data.e = 'ad-rotate';
      delete (post_message.data.pred_data.event);
      rotate_ad(post_message.data.pred_data);
      return true;
    }

    /**
     * When the passback sends an event, it will send it twice. The first person
     * to respond to the first time it is sent will be sent another message that
     * indicates to store the event. This is to prevent duplicate events in the
     * case that there are multiple handlers listening on the same page.
     */

    // Handle a postMessage from the passback (first message)
    if (
      post_message.data.action === 'pred_tracker' && // Data is from the PREDICT tracker
      typeof post_message.data.messageId !== 'undefined' && // Data includes a message ID
      (typeof post_message.data.send === 'undefined' || !post_message.data.send) // The message isn't marked to be sent
    ) {
      // Send the same object back to the sender to verify that it is only sent once
      post_message.source.postMessage(post_message.data, '*');
      return true;
    }

    // Handle a postMessage from the passback (second message)
    if (
      post_message.data.action === 'pred_tracker' && // Data is form the PREDICT tracker
      typeof post_message.data.messageId !== 'undefined' && // Data includes a message ID
      post_message.data.send // Data should be sent
    ) {
      // Change the event key
      post_message.data.e = post_message.data.event;
      delete post_message.data.event;

      // Add the event
      add_event(post_message.data);
      return true;
    }

    // handle a rotation event from someone else
    if (post_message.data.action === 'pred_interaction') {
      rotate_ad({
        e: post_message.data.action,
      });
    }
  } // --> post_message_handler

  /**
   * Determine whether to send a message to the ad zone to tell it to rotate on
   * interaction
   * @param  {Object}  data The `pred_data` from the postmessage
   * @return {Boolean}      Whether the message was sent or not
   */
  function rotate_ad(data) {
    log.trace('rotate_ad', arguments);

    // Check for igloo_id and e
    if (
      !igloo.utils.arrayHasKeys(data, ['e']) ||
      (
        data.e !== 'pred_interaction'
        && data.e !== 'ad-rotate'
      )
    ) {
      return false;
    }

    // Send the interaction event
    var igloo_keys;
    if (!igloo.utils.arrayHasKeys(data, ['igloo_id'])) {
      // Loop through all of the keys
      igloo_keys = Object.keys(igloo.ad_zones);
    } else {
      // Only the sepcified key
      igloo_keys = [data.igloo_id];
    }

    // Loop through the keys
    for (var i = 0; i < igloo_keys.length; i++) {
      // rotate ad if embed is configured to rotate on interaction and unit is not vuukle or didna test
      if (
        typeof igloo.ad_zones[igloo_keys[i]].rotateAd !== 'undefined'
        && !igloo.ad_zones[igloo_keys[i]].isProductOnlyEmbed
        && !igloo.ad_zones[igloo_keys[i]].isVuukleTestEmbed
        && !igloo.ad_zones[igloo_keys[i]].isDidnaTestEmbed
        && !igloo.ad_zones[igloo_keys[i]].isSovrnGoldEmbed
        && !igloo.ad_zones[igloo_keys[i]].isVdoAiEmbed
        && !igloo.ad_zones[igloo_keys[i]].isMilleniumEmbed
      ) {
        var topBid = igloo.ad_zones[igloo_keys[i]].getTopBid();

        if (topBid && topBid.mediaType === 'video') {
          // do not rotate on video ads
        } else {
          igloo.ad_zones[igloo_keys[i]].rotateAd();
        }
      }
    }
    return true;
  } // --> rotate_ad

  /**
   * Determine whether interaction is first o
   * @param {Object} data 
   */
  function check_if_first_interaction(data) {
    log.trace('check_if_first_interaction', arguments);

    // Check for igloo_id and e
    if (
      !igloo.utils.arrayHasKeys(data, ['e']) ||
      (data.e !== 'widget_interaction' && data.e !== 'pred_interaction')
    ) {
      return false;
    }

    var igloo_keys;
    if (!igloo.utils.arrayHasKeys(data, ['igloo_id'])) {
      // Loop through all of the keys
      igloo_keys = Object.keys(igloo.ad_zones);
    } else {
      // Only the specified key
      igloo_keys = [data.igloo_id];
    }

    var isFirst = false;
    // Loop through the keys
    for (var i = 0; i < igloo_keys.length; i++) {
      // if ad zone is found, check if first interaction
      if (typeof igloo.ad_zones[igloo_keys[i]].checkIfFirstInteraction !== 'undefined') {
        isFirst = igloo.ad_zones[igloo_keys[i]].checkIfFirstInteraction();
        break;
      }
    }
    return isFirst;
  } // --> check_if_first_interaction

  /**
   * Get embedId of ad zone based off of igloo_id
   * @param {Object} data 
   */
  function get_igloo_embed_id(data) {
    var igloo_keys;
    if (!igloo.utils.arrayHasKeys(data, ['igloo_id'])) {
      // can not reliably find embed for analytics, exit
      return null;
    } else {
      // Only the specified key
      igloo_keys = [data.igloo_id];
    }

    var embedId = null;
    for (var i = 0; i < igloo_keys.length; i++) {
      // if ad zone is found, check if first interaction
      if (typeof igloo.ad_zones[igloo_keys[i]].embedId !== 'undefined') {
        embedId = igloo.ad_zones[igloo_keys[i]].embedId;
        break;
      }
    }

    return embedId;
  } // --> get_igloo_embed_id

  // Start the interval that checks for events
  igloo.timer.registerFunction(check_events);

  // Start the post message handler
  igloo.top.addEventListener('message', post_message_handler, false);

  return {
    // Needed for external calls
    addEvent: add_event,
    enableDwell: enable_dwell,
    queue: queue,

    // Exported for testing
    send_events: send_events,
    send_event_handler: send_event_handler,
    add_ensighten_pixel: add_ensighten_pixel,
    add_nuviad_pixel: add_nuviad_pixel,
    add_getintent_pixel: add_getintent_pixel,
    add_comet_pixel: add_comet_pixel,
    update_object_data: update_object_data,
    embed_dwell_events: embed_dwell_events,
    post_message_handler: post_message_handler,
    rotate_ad: rotate_ad,
  };
};
